/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../../base.component';
import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { LanguageService, UserService, UtilsService, ValidationService } from '../../../services';
import { ModulesEnum } from '../../../../../environments/environment';
import { complexPasswordValidation, nameValidation } from '../../../directives/validation';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-rbac-users',
    templateUrl: './rbac-users.component.html',
    styleUrls: ['./rbac-users.component.scss']
})
export class RbacUsersComponent extends BaseComponent implements OnInit {
    /**
     * Get all the steps from the view, so we can scroll to the active step.
     */
    @ViewChildren('step') public steps: QueryList<any> = new QueryList<any>();

    /**
     * Incoming data from parent component.
     */
    @Input() set data(data: { users: any, roles: any, groups: any, userPatch?: any }) {
        this.roles = data['roles'];
        this.groups = data['groups'];
        this.user = data['userPatch'];

        this.editId = data.userPatch?.id ? data.userPatch.id : null;
        if (this.editId) {
            this.patchValuesToForm(data.userPatch);
            this.activeStep = this.moduleIsPortal ? 2 : 1;
        }

        this.setPageTitle();
    }

    /**
     * Tell parent to submit.
     */
    @Output() public submitUser: EventEmitter<any> = new EventEmitter();

    /**
     * Store the active step.
     */
    public activeStep: number = 1;

    /**
     * Store the edit id.
     */
    public editId: string;

    /**
     * User form.
     */
    public userForm: FormGroup;

    /**
     * Set selected roles.
     */
    public selectedRoles: any[] = [];

    /**
     * Set selected groups.
     */
    public selectedGroups: any[] = [];

    /**
     * Flag for checking active module.
     */
    public moduleIsPortal: boolean = false;

    /**
     * Store page title.
     */
    public pageTitle: string = '';

    /**
     * Flag to hide roles and groups for system users
     */
    public isSystemUser: boolean = false;

    /**
     * Store the user to edit.
     * @private
     */
    private user: any;

    /**
     * To store the initial form data.
     * @private
     */
    private initialFormData: any = {};

    /**
     * Users, Roles, groups variables for getter/setter
     * @private
     */
    private _users: any[] = [];
    private _roles: any[] = [];
    private _groups: any[] = [];

    constructor(
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.moduleIsPortal = this.environment.module === ModulesEnum.CENTRALISED_MANAGEMENT;

        this.setUpForm();
        this.setUpFormSubscribers();
    }

    /**
     * Setter/getter for users.
     */
    set users(users: any[]) {
        this._users = users;
    }

    get users(): any[] {
        return this._users;
    }

    /**
     * Setter/getter for roles.
     */
    set roles(roles: any[]) {
        this._roles = roles;
        this.addRolesToForm();
    }

    get roles(): any[] {
        return this._roles;
    }

    /**
     * Setter/getter for groups.
     */
    set groups(groups: any[]) {
        this._groups = groups;
        this.addGroupsToForm();
    }

    get groups(): any[] {
        return this._groups;
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get formRoles(): FormArray {
        return this.userForm.get('step2.roles') as FormArray;
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get formGroups(): FormArray {
        return this.userForm.get('step3.groups') as FormArray;
    }

    public ngOnInit(): void {
        this.toggleFields();
    }

    /**
     * Go back.
     */
    public back(): void {
        if (this.isSystemUser) {
            this.activeStep = 1;
        } else {
            this.activeStep--;
        }
    }

    /**
     * Next step.
     */
    public next(): void {
        if (this.isSystemUser) {
            this.activeStep = 4;
        } else {
            this.activeStep++;
        }
    }

    /**
     * Only show the warning banner if both the roles and groups are not set.
     */
    public showRolesGroupsWarning(): boolean {
        const roles: any = this.userForm.get('step2.roles').value;
        const groups: any = this.userForm.get('step3.groups').value;
        const activeRoles: any = roles.some((item: any): any => item.value);
        const activeGroups: any = groups.some((item: any): any => item.value);

        return !activeRoles && !activeGroups;
    }

    /**
     * Get selected items as a string.
     * @param itemsArray
     */
    public getSelectedItemsAsString(itemsArray: any): string {
        const selectedItems: any = itemsArray.filter((item: any): any => item.value);
        const names: any = selectedItems.map((item: {
            name: any;
        }): string => this.utilsService.firstLetterUppercase(item.name)).toString();

        return this.utilsService.replaceLastCommaWithAnd(names.toString(), true);
    }

    /**
     * Submit helper.
     */
    public submitHelper(): void {
        const userValues = this.userForm.getRawValue();
        if (this.editId) {
            userValues['passwordChanged'] = this.passwordChanged();
        }

        this.submitUser.emit(userValues);
    }

    /**
     * Get a field from the form group.
     * @param name
     */
    public getFormGroupName(name: string): any {
        return this.userForm.get(name);
    }

    /**
     * Get concatenated name.
     */
    public getConcatName(): string {
        const firstName: any = this.getFormGroupName('step1.firstName').value;
        const lastName: any = this.getFormGroupName('step1.lastName').value;

        return firstName + ' ' + lastName;
    }

    /**
     * Returns the form group as FormGroup.
     * @param group
     */
    public getFormGroup(group: any): FormGroup {
        return this.userForm.get(group) as FormGroup;
    }

    /**
     * Go to a specific step in the process.
     * @param number
     */
    public setStep(number: number): void {
        this.activeStep = number;
        this.scrollToActiveStep();
    }

    /**
     * Is next button disabled.
     */
    public isNextButtonDisabled(): boolean {
        if (this.activeStep === 3) {
            return !this.hasGroupsOrRoles();
        }

        if (this.activeStep === 4) {
            return !this.editId && this.moduleIsPortal && !this.userForm.get('step4.confirmKeyCopied').value;
        }

        return this.userForm.get('step' + this.activeStep.toString()).invalid;
    }

    /**
     * Is submit button disabled.
     */
    public isSubmitDisabled(): boolean {
        if (this.moduleIsPortal) {
            if (!this.editId) {
                return this.userForm.invalid || this.userForm.pristine;
            } else {
                return this.userForm.invalid || this.userForm.pristine;
            }
        } else {
            return this.userForm.invalid || this.userForm.pristine;
        }
    }

    /**
     * Check if the user has updated the password.
     * @private
     */
    private passwordChanged(): boolean {
        return !this.userForm.get('step1.password').pristine || this.userForm.get('step1.password').touched;
    }

    /**
     * Set title for page.
     * @private
     */
    private setPageTitle(): void {
        if (this.editId) {
            this.pageTitle = this.moduleIsPortal ? 'rbac_users_edit_user_permissions' : 'rbac_users_edit_user';
        } else {
            this.pageTitle = this.moduleIsPortal ? 'rbac_users_invite_user' : 'rbac_users_add_user';
        }
    }

    /**
     * Patch values to form.
     * @param user
     * @private
     */
    private patchValuesToForm(user: any): void {
        const patch: any = this.getPatchedValues(user);
        this.userForm.patchValue(patch);
        this.toggleFields();
        this.initialFormData = this.userForm.value;

        if (user.firstName === 'System' && user.lastName === 'Admin') {
            const step1 = this.userForm.get('step1');
            step1.get('firstName').disable();
            step1.get('lastName').disable();
            this.isSystemUser = true;
        }

        this.userForm.markAsPristine();
        this.userForm.updateValueAndValidity();
    }

    /**
     * Get patch for Portal form.
     * @param user
     * @private
     */
    private getPatchedValues(user: any): any {
        const formValues: any = this.userForm.value;

        return {
            step1: {
                id: user.id,
                orgId: user.orgId,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                confirmEmail: user.email,
                disableUser: user.disabled || false
            },
            step2: { roles: this.updateValueInArray(formValues.step2.roles, user.roles) },
            step3: { groups: this.updateValueInArray(formValues.step3.groups, user.groups) }
        };
    }

    /**
     * Set form array values.
     * @param formValues
     * @param valuesArray
     * @private
     */
    private updateValueInArray(formValues: any, valuesArray: any[]): any {
        if (valuesArray?.length) {
            for (const item of formValues) {
                if (valuesArray.includes(item.id)) {
                    item.value = true;
                }
            }
        }

        return formValues;
    }

    /**
     * Check if at least one role or group has been selected.
     * @private
     */
    private hasGroupsOrRoles(): boolean {
        const rolesSet: boolean = this.hasSelection(this.userForm.get('step2.roles').value);
        const groupsSet: boolean = this.hasSelection(this.userForm.get('step3.groups').value);

        return rolesSet || groupsSet;
    }

    /**
     * Check array for selected item.
     * @param array
     * @private
     */
    private hasSelection(array: any[]): boolean {
        return !!array.find((item: any): boolean => item.value === true);
    }

    /**
     * Toggle form fields.
     * @private
     */
    private toggleFields(): void {
        if (!this.moduleIsPortal) {
            this.userForm.get('step1.firstName').enable();
            this.userForm.get('step1.lastName').enable();

            const currentUserId = this.userService.getUser('id');
            const disabledUser: boolean = this.editId === currentUserId;
            if (disabledUser) {
                this.userForm.get('step1.disableUser').disable();
            } else {
                this.userForm.get('step1.disableUser').enable();
            }

            if (!this.editId) {
                this.userForm.get('step1.password').enable();
            }
            this.userForm.get('step1.confirmEmail').disable();
        } else {
            if (!this.editId) {
                this.userForm.get('step1.confirmEmail').enable();
            }
        }
    }

    /**
     * Setup form group.
     * @private
     */
    private setUpForm(): void {
        this.userForm = this.formBuilder.group({
            step1: this.formBuilder.group({
                id: [null],
                orgId: [null],
                email: ['', [Validators.required, ValidationService.emailValidator]],
                confirmEmail: [{ value: '', disabled: true }, [Validators.required, ValidationService.emailValidator]],
                password: [{ value: null, disabled: true }, [Validators.required, complexPasswordValidation]],
                firstName: [{
                    value: null,
                    disabled: true
                }, [Validators.required, Validators.maxLength(254), nameValidation]],
                lastName: [{
                    value: null,
                    disabled: true
                }, [Validators.required, Validators.maxLength(254), nameValidation]],
                disableUser: [{ value: false, disabled: true }, Validators.required]
            }),
            step2: this.formBuilder.group({
                roles: this.formBuilder.array([])
            }),
            step3: this.formBuilder.group({
                groups: this.formBuilder.array([])
            })
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.userForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: any): void => {
                    this.setSelectedRoles();
                    this.setSelectedGroups();
                    if (JSON.stringify(this.initialFormData) === JSON.stringify(values)) {
                        this.userForm.markAsPristine();
                    } else {
                        if (this.editId) {
                            this.userForm.get('step2.roles').markAsDirty();
                            this.userForm.get('step3.groups').markAsDirty();
                        } else {
                            this.userForm.markAsDirty();
                        }
                    }
                }
            });

        this.userForm.get('step1.email').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (email: string): void => {
                    this.validateEmail(email);
                }
            });

        this.userForm.get('step1.confirmEmail').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (confirmEmail: string): void => {
                    this.confirmEmail(confirmEmail);
                }
            });

        const password: AbstractControl = this.userForm.get('step1.password');
        password.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (value: string): void => {
                    this.utilsService.debounce('password', (): void => {
                        if (value?.length > 0) {
                            password.markAsTouched();
                            password.setValidators([Validators.required, complexPasswordValidation]);
                        } else {
                            if (this.editId) {
                                password.markAsPristine();
                                password.markAsUntouched();
                                password.clearValidators();
                            }
                        }
                        password.updateValueAndValidity({ emitEvent: false });
                    }, 500);
                }
            });
    }

    /**
     * Validate username.
     * @param email
     * @private
     */
    private validateEmail(email: string): void {
        const foundEmail: boolean = !!this.users.find((user: any): boolean => user.email === email && this.user?.email !== email);
        const emailField: AbstractControl = this.userForm.get('email');
        if (foundEmail) {
            emailField.setErrors({ 'duplicateEmail': `${this.languageService.translate('email_in_use')}` });
            emailField.markAsTouched();
        } else {
            emailField.setErrors(null);
        }
    }

    /**
     * Confirm email.
     * @param confirmEmail
     * @private
     */
    private confirmEmail(confirmEmail: string): void {
        const email: string = this.userForm.get('step1.email').value;
        const confirmEmailForm: AbstractControl = this.userForm.get('step1.confirmEmail');
        if (confirmEmail !== email) {
            confirmEmailForm.setErrors({ 'mismatch': `${this.languageService.translate('email_mismatch')}` });
            confirmEmailForm.markAsTouched();
        } else {
            confirmEmailForm.setErrors(null);
        }
    }

    /**
     * Set selected roles.
     * @private
     */
    private setSelectedRoles(): void {
        const roles: any = this.userForm.get('step2.roles').value;
        this.selectedRoles = roles.filter((item: any): any => item.value);
    }

    /**
     * Set selected groups
     * @private
     */
    private setSelectedGroups(): void {
        const groups: any = this.userForm.get('step3.groups').value;
        this.selectedGroups = groups.filter((item: any): any => item.value);
    }

    /**
     * Add roles to form.
     * @private
     */
    private addRolesToForm(): void {
        for (const role of this.roles) {
            this.formRoles.push(
                this.formBuilder.group(
                    {
                        id: role.id,
                        name: role.name,
                        value: role.value || false
                    }
                )
            );
        }
    }

    /**
     * Add groups to form controls.
     * @private
     */
    private addGroupsToForm(): void {
        for (const group of this.groups) {
            this.formGroups.push(
                this.formBuilder.group(
                    {
                        id: group.id,
                        name: group.name,
                        value: group.value || false
                    }
                )
            );
        }
    }

    /**
     * Scroll to active step.
     * @private
     */
    private scrollToActiveStep(): void {
        const elements: any[] = this.steps.toArray();
        elements[this.activeStep - 1].nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
        });
    }
}
